import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Eye, CreditCard, Server, Users, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function DataSecurity() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b1220] transition-colors duration-200">
      {/* Header bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-teal-600" />
            <h1 className="text-lg font-bold text-slate-800">PFD Analysis</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-500 hover:text-slate-700"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-50 border-2 border-teal-200 mb-5">
            <Shield className="w-8 h-8 text-teal-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3" data-testid="security-page-title">
            Data Security & Trust Commitment
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Your Data. Your Control. No Exceptions.
          </p>
        </div>

        {/* Principle statement */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-8">
          <p className="text-base text-slate-700 leading-relaxed">
            PFD Analysis is built on a simple principle: <strong className="text-slate-900">your production data is yours — and it stays yours.</strong>
          </p>
          <p className="text-base text-slate-700 leading-relaxed mt-3">
            We do not share, reuse, or benchmark your data across farms unless you explicitly choose to do so.
          </p>
        </div>

        {/* Commercial sensitivity */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-3">Built for Commercial Sensitivity</h2>
          <p className="text-sm text-slate-600 mb-4">We understand what your data represents:</p>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 shrink-0" />
              Biomass and stock levels
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 shrink-0" />
              Feed performance and efficiency
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1.5 shrink-0" />
              Mortality and operational decisions
            </li>
          </ul>
          <p className="text-sm text-slate-600 mt-4 italic">
            This is not just data — it is competitive intelligence. That is why PFD Analysis is designed with strict data isolation and protection at its core.
          </p>
        </div>

        {/* How we protect — grid */}
        <h2 className="text-xl font-bold text-slate-900 mb-5">How We Protect Your Data</h2>

        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          {/* Full Data Isolation */}
          <div className="bg-white rounded-xl border-l-4 border-l-teal-500 border border-slate-200 shadow-sm p-5" data-testid="isolation-card">
            <div className="flex items-center gap-2.5 mb-3">
              <Lock className="w-5 h-5 text-teal-600" />
              <h3 className="text-sm font-bold text-slate-900">Full Data Isolation</h3>
            </div>
            <p className="text-xs text-slate-500 mb-2">Every account is fully segregated.</p>
            <ul className="space-y-1.5 text-xs text-slate-600">
              <li>One farm can never access another farm's data</li>
              <li>No shared datasets</li>
              <li>No cross-customer visibility</li>
            </ul>
          </div>

          {/* Secure Access Control */}
          <div className="bg-white rounded-xl border-l-4 border-l-indigo-500 border border-slate-200 shadow-sm p-5" data-testid="access-card">
            <div className="flex items-center gap-2.5 mb-3">
              <Users className="w-5 h-5 text-indigo-600" />
              <h3 className="text-sm font-bold text-slate-900">Secure Access Control</h3>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600">
              <li>Encrypted login and session handling</li>
              <li>Strong password protection standards</li>
              <li>Optional multi-factor authentication for enhanced security</li>
            </ul>
          </div>

          {/* Secure Data Handling */}
          <div className="bg-white rounded-xl border-l-4 border-l-emerald-500 border border-slate-200 shadow-sm p-5" data-testid="handling-card">
            <div className="flex items-center gap-2.5 mb-3">
              <Server className="w-5 h-5 text-emerald-600" />
              <h3 className="text-sm font-bold text-slate-900">Secure Data Handling</h3>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600">
              <li>All data encrypted in transit (HTTPS)</li>
              <li>Secure storage of uploaded files and production datasets</li>
              <li>Strict validation of all uploaded spreadsheets and models</li>
            </ul>
          </div>

          {/* Controlled Access & Auditability */}
          <div className="bg-white rounded-xl border-l-4 border-l-amber-500 border border-slate-200 shadow-sm p-5" data-testid="audit-card">
            <div className="flex items-center gap-2.5 mb-3">
              <Eye className="w-5 h-5 text-amber-600" />
              <h3 className="text-sm font-bold text-slate-900">Controlled Access & Auditability</h3>
            </div>
            <ul className="space-y-1.5 text-xs text-slate-600">
              <li>All key actions are logged and traceable</li>
              <li>Administrative access is restricted and monitored</li>
              <li>Full accountability on data access and changes</li>
            </ul>
          </div>
        </div>

        {/* Payments */}
        <div className="bg-white rounded-xl border-l-4 border-l-violet-500 border border-slate-200 shadow-sm p-5 mb-8" data-testid="payments-card">
          <div className="flex items-center gap-2.5 mb-3">
            <CreditCard className="w-5 h-5 text-violet-600" />
            <h3 className="text-sm font-bold text-slate-900">Secure Payments & Credit System</h3>
          </div>
          <ul className="space-y-1.5 text-xs text-slate-600">
            <li>Payments handled via trusted providers (Stripe)</li>
            <li>No payment data stored within the platform</li>
            <li>Credits are securely managed server-side — cannot be manipulated</li>
          </ul>
        </div>

        {/* Trust section */}
        <div className="bg-teal-50 rounded-xl border border-teal-200 p-6 sm:p-8 mb-8">
          <h2 className="text-xl font-bold text-teal-900 mb-3">Designed for Trust, Not Just Compliance</h2>
          <p className="text-sm text-teal-800 mb-4">
            PFD Analysis is not just technically secure — it is commercially aligned with your interests:
          </p>
          <ul className="space-y-2 text-sm text-teal-800">
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 shrink-0" />
              You retain full ownership of your data
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 shrink-0" />
              You can request deletion of your data at any time
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1.5 shrink-0" />
              Your data is never used to benefit another operation
            </li>
          </ul>
        </div>

        {/* Built for */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-3">Built for Professional Operations</h2>
          <p className="text-sm text-slate-600 mb-3">The platform is designed to meet the expectations of:</p>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
              Commercial aquaculture producers
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
              Technology partners and integrators
            </li>
            <li className="flex items-start gap-2.5">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-1.5 shrink-0" />
              Data-sensitive operations requiring full confidentiality
            </li>
          </ul>
        </div>

        {/* Commitment */}
        <div className="bg-slate-900 rounded-xl p-6 sm:p-8 mb-8 text-center">
          <h2 className="text-xl font-bold text-white mb-3">Our Commitment</h2>
          <p className="text-sm text-slate-300 mb-4">
            We are continuously improving our security framework as the platform grows.
          </p>
          <p className="text-base text-teal-400 font-semibold">
            Protect your data first. Everything else comes second.
          </p>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 text-center" data-testid="summary-card">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Summary</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="bg-teal-50 rounded-lg px-5 py-3 border border-teal-200">
              <p className="text-sm font-semibold text-teal-800">Your data is isolated</p>
            </div>
            <div className="bg-teal-50 rounded-lg px-5 py-3 border border-teal-200">
              <p className="text-sm font-semibold text-teal-800">Your data is protected</p>
            </div>
            <div className="bg-teal-50 rounded-lg px-5 py-3 border border-teal-200">
              <p className="text-sm font-semibold text-teal-800">Your data is never shared</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-5">
            PFD Analysis gives you decision intelligence — without compromising control.
          </p>
        </div>
      </div>
    </div>
  );
}
